import type {
  Device,
  ErrorSummary,
  FlightActivity,
  MemoryDataPoint,
  WiFiEvent,
  OTAUpdate,
  EventRate,
  AppInsightsConfig,
} from '../types';

const API_BASE = 'https://api.applicationinsights.io/v1/apps';

class AppInsightsService {
  private config: AppInsightsConfig | null = null;

  setConfig(config: AppInsightsConfig) {
    this.config = config;
  }

  private async query<T>(query: string): Promise<T[]> {
    if (!this.config) {
      throw new Error('App Insights not configured');
    }

    const response = await fetch(
      `${API_BASE}/${this.config.appId}/query?query=${encodeURIComponent(query)}`,
      {
        headers: {
          'X-Api-Key': this.config.apiKey,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Query failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.tables[0]?.rows || [];
  }

  async getFleetHealth(): Promise<Device[]> {
    const query = `
      customEvents
      | extend
          DeviceId = tostring(customDimensions.device_id),
          DeviceName = tostring(customDimensions.device_name)
      | summarize arg_max(timestamp, *) by DeviceId, DeviceName
      | extend
          LastSeen = timestamp,
          Version = tostring(customDimensions.version),
          SSID = tostring(customDimensions.ssid),
          MemFree = toint(customDimensions.mem_free)
      | extend MinutesAgo = datetime_diff('minute', now(), LastSeen)
      | extend Status = case(
          MinutesAgo < 5, "Online",
          MinutesAgo < 60, "Recent",
          "Offline"
        )
      | project DeviceName, DeviceId, Status, LastSeen, Version, SSID, MemFree
      | order by LastSeen desc
    `;

    const rows = await this.query<any[]>(query);
    return rows.map(row => ({
      deviceName: row[0],
      deviceId: row[1],
      status: row[2],
      lastSeen: new Date(row[3]),
      version: row[4],
      ssid: row[5],
      memFree: row[6],
    }));
  }

  async getErrors(): Promise<ErrorSummary[]> {
    const query = `
      customEvents
      | where customDimensions.severity == "ERROR"
      | where timestamp > ago(1d)
      | summarize ErrorCount = count() by
          EventName = name,
          Device = tostring(customDimensions.device_name)
      | order by ErrorCount desc
    `;

    const rows = await this.query<any[]>(query);
    return rows.map(row => ({
      eventName: row[0],
      device: row[1],
      errorCount: row[2],
    }));
  }

  async getFlightActivity(): Promise<FlightActivity[]> {
    const query = `
      customEvents
      | where name == "flight_found"
      | where timestamp > ago(1d)
      | project
          timestamp,
          Device = tostring(customDimensions.device_name),
          FlightCode = tostring(customDimensions.flight_code),
          FlightId = tostring(customDimensions.flight_id),
          Altitude = toint(customDimensions.altitude),
          Speed = toint(customDimensions.speed)
      | order by timestamp desc
      | take 50
    `;

    const rows = await this.query<any[]>(query);
    return rows.map(row => ({
      timestamp: new Date(row[0]),
      device: row[1],
      flightCode: row[2],
      flightId: row[3],
      altitude: row[4],
      speed: row[5],
    }));
  }

  async getMemoryTrends(): Promise<MemoryDataPoint[]> {
    const query = `
      customEvents
      | where isnotnull(customDimensions.mem_free)
      | where timestamp > ago(1d)
      | project
          timestamp,
          Device = tostring(customDimensions.device_name),
          MemFree = toint(customDimensions.mem_free)
      | order by timestamp asc
    `;

    const rows = await this.query<any[]>(query);
    return rows.map(row => ({
      timestamp: new Date(row[0]),
      device: row[1],
      memFree: row[2],
    }));
  }

  async getWiFiEvents(): Promise<WiFiEvent[]> {
    const query = `
      customEvents
      | where name in ("wifi_connected", "wifi_failed")
      | where timestamp > ago(90d) // ~3 months
      | extend Device = tostring(customDimensions.device_name)
      | summarize arg_max(timestamp, *) by Device
      | project
          timestamp,
          Device,
          Event = name,
          SSID = tostring(customDimensions.ssid),
          IP = tostring(customDimensions.ip),
          Severity = tostring(customDimensions.severity)
      | order by timestamp desc
    `;

    const rows = await this.query<any[]>(query);
    return rows.map(row => ({
      timestamp: new Date(row[0]),
      device: row[1],
      event: row[2],
      ssid: row[3],
      ip: row[4],
      severity: row[5],
    }));
  }

  async getOTAUpdates(): Promise<OTAUpdate[]> {
    const query = `
      customEvents
      | where name in ("ota_update", "ota_skip")
      | where timestamp > ago(7d)
      | project
          timestamp,
          Device = tostring(customDimensions.device_name),
          Event = name,
          OldVersion = tostring(customDimensions.old_version),
          NewVersion = tostring(customDimensions.new_version),
          CurrentVersion = tostring(customDimensions.current)
      | order by timestamp desc
    `;

    const rows = await this.query<any[]>(query);
    return rows.map(row => ({
      timestamp: new Date(row[0]),
      device: row[1],
      event: row[2],
      oldVersion: row[3],
      newVersion: row[4],
      currentVersion: row[5],
    }));
  }

  async getEventRates(): Promise<EventRate[]> {
    const query = `
      customEvents
      | where timestamp > ago(1d)
      | summarize Count = count() by bin(timestamp, 1h), EventName = name
      | order by timestamp asc
    `;

    const rows = await this.query<any[]>(query);
    return rows.map(row => ({
      timestamp: new Date(row[0]),
      eventName: row[1],
      count: row[2],
    }));
  }
}

export const appInsightsService = new AppInsightsService();
